/* global Image */

/**
 * Share Puzzle Image Generator
 * Generates a shareable image for Puzzle game results using HTML5 Canvas
 *
 * @param {Object} puzzleData - Puzzle game information
 * @param {string} puzzleData.difficulty - Difficulty level ('easy'|'medium'|'hard')
 * @param {string} puzzleData.imageSrc - Puzzle image source (from gameSnapshot)
 * @param {Object} translations - Translation object (t)
 * @param {string} theme - Theme ('light'|'dark')
 * @returns {Promise<Blob>} - Image blob ready for sharing
 */
export async function generatePuzzleResultImage (puzzleData, translations, theme = 'light') {
  // Canvas dimensions (same as Memorama - 1080x1080 Instagram format)
  const CANVAS_WIDTH = 1080
  const CANVAS_HEIGHT = 1080

  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_WIDTH
  canvas.height = CANVAS_HEIGHT
  const ctx = canvas.getContext('2d')

  // Colors (same as Memorama)
  const COLORS = {
    background: theme === 'light' ? '#43a1d5' : '#173e69',
    backgroundEnd: theme === 'light' ? '#173e69' : '#2a2a2a',
    cardBackground: '#ffffff',
    textPrimary: '#173e69',
    textSecondary: '#666666',
    accent: '#f0c430'
  }

  try {
    // 1. Draw background gradient (same as Memorama)
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT)
    gradient.addColorStop(0, COLORS.background)
    gradient.addColorStop(1, COLORS.backgroundEnd)
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    // 2. Draw main card container (same as Memorama)
    const cardPadding = 80
    const cardX = cardPadding
    const cardY = cardPadding
    const cardWidth = CANVAS_WIDTH - (cardPadding * 2)
    const cardHeight = CANVAS_HEIGHT - (cardPadding * 2)

    ctx.fillStyle = COLORS.cardBackground
    ctx.shadowColor = 'rgb(0 0 0 / 0.2)'
    ctx.shadowBlur = 20
    ctx.shadowOffsetY = 10
    roundRect(ctx, cardX, cardY, cardWidth, cardHeight, 20)
    ctx.fill()
    ctx.shadowColor = 'transparent'

    // 3. Draw Memodiez logo
    await drawLogo(ctx, CANVAS_WIDTH / 2, cardY + 100, 200, theme)

    // 4. Draw puzzle image (SQUARE, not circular)
    const imageSize = 200
    const imageX = (CANVAS_WIDTH - imageSize) / 2
    const imageY = cardY + 150

    await drawPuzzleImage(ctx, puzzleData.imageSrc, imageX, imageY, imageSize)

    // 5. Draw puzzle completion text
    const textY = imageY + imageSize + 80
    await drawPuzzleText(ctx, puzzleData, translations, textY, COLORS)

    // 6. Draw call to action
    const ctaY = CANVAS_HEIGHT - 250
    drawCallToAction(ctx, translations, ctaY, COLORS)

    // 7. Draw footer with website
    ctx.fillStyle = COLORS.textSecondary
    ctx.font = '32px Arial, sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('memodiez.com', CANVAS_WIDTH / 2, CANVAS_HEIGHT - 120)

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob)
      }, 'image/png', 0.9)
    })
  } catch (error) {
    console.error('Error generating puzzle share image:', error)
    throw new Error('Failed to generate puzzle share image')
  }
}

/**
 * Draw rounded rectangle helper (reused from Memorama)
 */
function roundRect (ctx, x, y, width, height, radius) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

/**
 * Draw Memodiez logo (reused from Memorama)
 */
async function drawLogo (ctx, centerX, centerY, maxWidth, theme) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const aspectRatio = img.width / img.height
      const logoWidth = Math.min(maxWidth, img.width)
      const logoHeight = logoWidth / aspectRatio

      const x = centerX - logoWidth / 2
      const y = centerY - logoHeight / 2

      if (theme === 'dark') {
        ctx.filter = 'invert(1)'
      }

      ctx.drawImage(img, x, y, logoWidth, logoHeight)
      ctx.filter = 'none'

      resolve()
    }

    img.onerror = () => {
      // Fallback: draw text
      ctx.fillStyle = '#173e69'
      ctx.font = 'bold 64px Arial, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('MEMODIEZ', centerX, centerY)
      resolve()
    }

    img.src = 'assets/logos/logo.webp'
  })
}

/**
 * Load and draw puzzle image (SQUARE, not circular)
 */
async function drawPuzzleImage (ctx, imageSrc, x, y, size) {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      // Save context
      ctx.save()

      // Draw square image with border
      ctx.drawImage(img, x, y, size, size)

      // Draw border around image (same style as Memorama avatar)
      ctx.strokeStyle = '#173e69'
      ctx.lineWidth = 4
      ctx.strokeRect(x, y, size, size)

      // Restore context
      ctx.restore()

      resolve()
    }

    img.onerror = () => {
      // Fallback: draw placeholder
      ctx.fillStyle = '#e0e0e0'
      ctx.fillRect(x, y, size, size)

      // Draw border
      ctx.strokeStyle = '#173e69'
      ctx.lineWidth = 4
      ctx.strokeRect(x, y, size, size)

      // Draw placeholder icon
      ctx.fillStyle = '#666666'
      ctx.font = `${size * 0.3}px Arial, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('🧩', x + size / 2, y + size / 2)

      resolve()
    }

    img.src = imageSrc
  })
}

/**
 * Draw puzzle completion text
 */
async function drawPuzzleText (ctx, puzzleData, translations, startY, colors) {
  const centerX = ctx.canvas.width / 2
  const lineHeight = 60

  // Get difficulty name from translations
  const difficultyKey = puzzleData.difficulty // 'easy', 'medium', 'hard'
  const difficultyName = translations.puzzle?.start?.selector?.[difficultyKey] || puzzleData.difficulty

  // Main text: "Resolví el rompecabezas maradoniano del día con dificultad {diff}"
  ctx.fillStyle = colors.textPrimary
  ctx.font = 'bold 36px Arial, sans-serif'
  ctx.textAlign = 'center'

  // Split text into multiple lines for better readability
  const line1 = translations.puzzle.share.text1 || 'Resolví el rompecabezas'
  const line2 = translations.puzzle.share.text2 || 'maradoniano del día'
  const line3 = translations.puzzle.share.text3 || 'con dificultad'

  ctx.fillText(line1, centerX, startY)
  ctx.fillText(line2, centerX, startY + lineHeight)
  ctx.fillText(line3, centerX, startY + lineHeight * 2)

  // Difficulty highlighted (same style as Memorama score)
  ctx.fillStyle = colors.accent
  ctx.font = 'bold 56px Arial, sans-serif'
  ctx.fillText(difficultyName, centerX, startY + lineHeight * 3 + 20)
}

/**
 * Draw call to action
 */
function drawCallToAction (ctx, translations, startY, colors) {
  const centerX = ctx.canvas.width / 2
  const lineHeight = 50

  ctx.fillStyle = colors.textPrimary
  ctx.font = 'bold 36px Arial, sans-serif'
  ctx.textAlign = 'center'

  // Line 1: "¿Podés conseguirlo vos?"
  const line1 = translations.puzzle.share.callToAction || '¿Podés conseguirlo vos?'
  ctx.fillText(line1, centerX, startY)

  // Line 2: "Intentalo gratis en"
  const line2 = translations.puzzle.share.tryFree || 'Intentalo gratis en'
  ctx.fillText(line2, centerX, startY + lineHeight)
}

/**
 * Generate share text for social media (Puzzle version)
 */
export function generatePuzzleShareText (puzzleData, translations) {
  const difficultyKey = puzzleData.difficulty
  const difficultyName = translations.puzzle?.start?.selector?.[difficultyKey] || puzzleData.difficulty

  const shareText = (translations.puzzle.share.text ||
    'Resolví el rompecabezas maradoniano del día con dificultad {difficulty}')
    .replace('{difficulty}', difficultyName)

  const callToAction = translations.puzzle.share.callToAction ||
    '¿Podés conseguirlo vos?'

  return `${shareText}\n\n${callToAction}\n\nhttps://memodiez.com`
}
